import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "../../utils/swal";
import { Camera, KeyRound, Settings2 } from "lucide-react";
import { AUTH, CONTENT_TYPE, UPLOADS_URL } from "../../config/constants";
import { Post } from "../../config/api/post";
import { addUser, updateUserData } from "../../redux/slice/authSlice";
import defaultAvatar from "../../assets/avatar.png";
import "../teachers/teachers.css";
import "./profile.css";

function ProfileAvatar({ user, editing, preview, onPick }) {
  const src = preview || (user?.image ? `${UPLOADS_URL}/${user.image}` : defaultAvatar);
  const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() || "A";
  const showImage = Boolean(preview || user?.image);

  return (
    <div className="profile-avatar-wrap">
      {showImage ? (
        <img className="profile-avatar" src={src} alt={`${user?.firstName || "Admin"} profile`} />
      ) : (
        <span className="profile-avatar-fallback">{initials}</span>
      )}
      {editing ? (
        <label className="profile-avatar-edit" aria-label="Change photo">
          <Camera size={15} />
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onPick(file);
            }}
          />
        </label>
      ) : null}
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
  });

  useEffect(() => {
    setForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
    });
  }, [user?.firstName, user?.lastName, user?.email]);

  const startEdit = () => {
    setForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
    });
    setImageFile(null);
    setImagePreview("");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setImageFile(null);
    setImagePreview("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      swal("Missing details", "First and last name are required.", "error");
      return;
    }

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
    };

    const formObject = new FormData();
    if (imageFile) formObject.append("image", imageFile);
    formObject.append("firstName", payload.firstName);
    formObject.append("lastName", payload.lastName);

    setSaving(true);
    Post(AUTH.updateProfile, formObject, token, null, CONTENT_TYPE.FORM_DATA)
      .then((response) => {
        const body = response?.data;
        if (body?.status) {
          const updatedUser = body.data || {};
          dispatch(updateUserData(updatedUser));
          if (token) {
            dispatch(addUser({ user: updatedUser, token }));
          }
          swal("Success!", body.message || "Profile updated successfully", "success");
          setEditing(false);
          setImageFile(null);
          setImagePreview("");
        } else {
          swal("Oops!", body?.message || "Update failed", "error");
        }
      })
      .catch((error) => {
        swal("Oops!", error?.data?.message || "Something went wrong", "error");
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="profile-page">
      <div className="profile-hero">
        <ProfileAvatar
          user={user}
          editing={editing}
          preview={imagePreview}
          onPick={(file) => {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
          }}
        />
        <div className="profile-hero-copy">
          <h1>
            {user?.firstName || "Admin"} {user?.lastName || ""}
          </h1>
          <p>{user?.email || "—"}</p>
        </div>
      </div>

      <div className="profile-card">
        <div className="profile-card-head">
          <h2>Account details</h2>
        </div>
        <form className="profile-card-body" onSubmit={handleSubmit}>
          <div className="profile-grid">
            <div className="profile-field">
              <label htmlFor="profile-first-name">First name</label>
              {editing ? (
                <input
                  id="profile-first-name"
                  value={form.firstName}
                  onChange={(event) => setForm((prev) => ({ ...prev, firstName: event.target.value }))}
                />
              ) : (
                <p>{user?.firstName || "—"}</p>
              )}
            </div>
            <div className="profile-field">
              <label htmlFor="profile-last-name">Last name</label>
              {editing ? (
                <input
                  id="profile-last-name"
                  value={form.lastName}
                  onChange={(event) => setForm((prev) => ({ ...prev, lastName: event.target.value }))}
                />
              ) : (
                <p>{user?.lastName || "—"}</p>
              )}
            </div>
            <div className="profile-field" style={{ gridColumn: "1 / -1" }}>
              <label htmlFor="profile-email">Email</label>
              {editing ? (
                <input id="profile-email" value={form.email} disabled />
              ) : (
                <p>{user?.email || "—"}</p>
              )}
            </div>
          </div>

          <div className="profile-actions">
            {editing ? (
              <>
                <button type="submit" className="data-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save changes"}
                </button>
                <button type="button" className="data-secondary" disabled={saving} onClick={cancelEdit}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button type="button" className="data-primary" onClick={startEdit}>
                  Edit profile
                </button>
                <button type="button" className="data-secondary" onClick={() => navigate("/profile/changePass")}>
                  <KeyRound size={16} />
                  Change password
                </button>
                <button type="button" className="data-secondary" onClick={() => navigate("/settings")}>
                  <Settings2 size={16} />
                  Settings
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
